import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const API = `${(typeof window !== "undefined" ? window.location.origin : "")}/api`;

function formalSalutation(c) {
  if (!c) return "Willkommen";
  const last = c.last_name || c.company_name || "";
  if (c.salutation === "herr") return `Willkommen, Herr ${last}`;
  if (c.salutation === "frau") return `Willkommen, Frau ${last}`;
  return `Willkommen, ${last}`;
}

export default function PortalSetup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/portal/setup/${token}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Einladung ungültig"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Passwort: mindestens 8 Zeichen"); return; }
    if (password !== password2) { toast.error("Passwörter stimmen nicht überein"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/portal/setup/${token}`, { password });
      setDone(true);
      toast.success("Passwort gesetzt – Sie können sich jetzt anmelden.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally { setLoading(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white border border-rose-200 rounded-2xl p-8 max-w-md w-full text-center shadow-sm" data-testid="setup-error">
          <WarningCircle size={48} className="mx-auto text-rose-500 mb-3" />
          <h1 className="text-xl font-semibold text-slate-900 mb-1">Einladung ungültig</h1>
          <p className="text-sm text-slate-600">{error}</p>
          <p className="text-xs text-slate-400 mt-4">Bitte wenden Sie sich an Ihre Hausverwaltung.</p>
        </div>
      </div>
    );
  }

  if (!info) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-sm text-slate-500">Einladung wird geladen…</div>;
  }

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white border border-emerald-200 rounded-2xl p-8 max-w-md w-full text-center shadow-sm" data-testid="setup-success">
          <CheckCircle size={48} className="mx-auto text-emerald-500 mb-3" weight="fill" />
          <h1 className="text-xl font-semibold text-slate-900 mb-1">Passwort gesetzt</h1>
          <p className="text-sm text-slate-600 mb-5">Sie können sich nun in Ihrem Eigentümerportal anmelden.</p>
          <Button className="bg-[#0F2A4D] hover:bg-[#0F2A4D]/90" onClick={() => navigate("/login")} data-testid="setup-go-login">
            Zum Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center px-4 py-8">
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 max-w-md w-full" data-testid="setup-form-card">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#0F2A4D]/10 mb-3">
            <Lock size={26} className="text-[#0F2A4D]" />
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">Eigentümerportal einrichten</h1>
          <p className="text-sm text-slate-600 mt-2" data-testid="setup-greeting">{formalSalutation(info.contact)}</p>
          {info.tenant?.legal_name && (
            <p className="text-xs text-slate-400 mt-1">{info.tenant.legal_name}</p>
          )}
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600 mb-5">
          <div className="flex justify-between"><span>E-Mail:</span><span className="font-mono">{info.email}</span></div>
          <div className="flex justify-between mt-1"><span>Gültig bis:</span><span>{new Date(info.expires_at).toLocaleDateString("de-DE")}</span></div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Neues Passwort (mind. 8 Zeichen)</Label>
            <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="setup-password" />
          </div>
          <div>
            <Label className="text-xs">Passwort wiederholen</Label>
            <Input type="password" autoComplete="new-password" value={password2} onChange={(e) => setPassword2(e.target.value)} required data-testid="setup-password-confirm" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#0F2A4D] hover:bg-[#0F2A4D]/90 mt-2" data-testid="setup-submit">
            {loading ? "Speichere…" : "Passwort setzen & Portal aktivieren"}
          </Button>
        </form>
      </div>
    </div>
  );
}
